<template>
  <Button
    v-if="isSupported"
    :disabled="disabled"
    @click="startShare"
    variant="ghost"
    class="group rounded-full px-2"
  >
    <div class="share-button !h-6 !w-6 cursor-pointer opacity-60 group-hover:opacity-100" />
  </Button>
  <DropdownMenu v-else>
    <DropdownMenuTrigger>
      <Button variant="ghost" class="group">
        <div
          class="share-button !h-6 !w-6 cursor-pointer opacity-60 group-hover:opacity-100"
        /> </Button
    ></DropdownMenuTrigger>
    <DropdownMenuContent>
      <DropdownMenuItem v-for="network in networks" :key="network.network">
        <ShareNetwork
          :network="network.network"
          :title="props.title"
          :description="stripHtml(props.text)"
          :media="props.media"
          :url="props.url"
          v-slot="{ share }"
        >
          <span @click="share">{{ t('common.shareOn') }} {{ network.name }}</span>
        </ShareNetwork>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>

<script setup lang="ts">
import { useShare } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
// Components
import Button from '@ui/button/Button.vue';
import { ShareNetwork } from 'vue3-social-sharing';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@ui/dropdown-menu';
import { useTelemetryFetcher } from '@api/tracking.api';

const { t } = useI18n();

const networks = [
  {
    network: 'facebook',
    name: 'Facebook',
  },
  {
    network: 'whatsapp',
    name: 'Whatsapp',
  },
  {
    network: 'email',
    name: 'Email',
  },
  {
    network: 'sms',
    name: 'SMS',
  },
];

interface ShareProps {
  title: string;
  text?: string;
  url: string;
  media?: string;
  disabled?: boolean;
}

const props = defineProps<ShareProps>();

const { share, isSupported } = useShare();

const stripHtml = (html: string | undefined): string => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const startShare = async () => {
  if (props.disabled) return;
  try {
    await share({
      title: props.title,
      text: stripHtml(props.text),
      url: props.url,
    });
    useTelemetryFetcher().execute({
      metric: 'share',
      resource: props.url,
    });
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      // Share was cancelled by user, we can handle this case if needed
      console.log('Share was cancelled');
    } else {
      // Handle other potential errors
      console.error('Error sharing:', error);
    }
  }
};
</script>

<style scoped>
.share-button {
  @apply bg-[url('../share_icon.svg')] bg-contain bg-center bg-no-repeat opacity-70 hover:opacity-100;
}
</style>
